import { useState } from 'react';
import { TrashIcon, ChatBubbleLeftIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import type { ClubTheme } from './themes';
import {
  useMyActivity,
  useDeleteMyPost,
  useDeleteMyComment,
  type ActivityRange,
  type MyActivityPost,
  type MyActivityComment,
} from './queries';

interface MyActivityProps {
  clubId: string;
  theme: ClubTheme;
  onNavigateToPost?: (postId: string) => void;
}

const RANGES: { value: ActivityRange; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '48h', label: '48h' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: 'all', label: 'All' },
];

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString();
}

export default function MyActivity({ clubId, theme, onNavigateToPost }: MyActivityProps) {
  const [range, setRange] = useState<ActivityRange>('7d');
  const [tab, setTab] = useState<'posts' | 'comments'>('posts');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data, isLoading } = useMyActivity(clubId, range);
  const deletePost = useDeleteMyPost(clubId);
  const deleteComment = useDeleteMyComment(clubId);

  const posts = data?.posts?.filter((p) => !p.is_deleted) || [];
  const comments = data?.comments?.filter((c) => !c.is_deleted) || [];

  const handleDeletePost = (postId: string) => {
    if (confirmDelete === postId) {
      deletePost.mutate(postId);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(postId);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    if (confirmDelete === commentId) {
      deleteComment.mutate(commentId);
      setConfirmDelete(null);
    } else {
      setConfirmDelete(commentId);
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  };

  return (
    <div
      className="rounded-2xl p-4"
      style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.muted}20` }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold" style={{ color: theme.colors.text }}>
          My Activity
        </h3>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              className="rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors"
              style={{
                backgroundColor: range === r.value ? theme.colors.primary : `${theme.colors.muted}15`,
                color: range === r.value ? theme.colors.bg : theme.colors.muted,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setTab('posts')}
          className="flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors"
          style={{
            backgroundColor: tab === 'posts' ? `${theme.colors.primary}20` : 'transparent',
            color: tab === 'posts' ? theme.colors.primary : theme.colors.muted,
          }}
        >
          <DocumentTextIcon className="h-3.5 w-3.5" />
          Posts ({posts.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('comments')}
          className="flex items-center gap-1 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors"
          style={{
            backgroundColor: tab === 'comments' ? `${theme.colors.primary}20` : 'transparent',
            color: tab === 'comments' ? theme.colors.primary : theme.colors.muted,
          }}
        >
          <ChatBubbleLeftIcon className="h-3.5 w-3.5" />
          Replies ({comments.length})
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="py-6 text-center">
          <div
            className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-t-transparent"
            style={{ borderColor: `${theme.colors.muted}40`, borderTopColor: theme.colors.primary }}
          />
        </div>
      )}

      {/* Posts tab */}
      {!isLoading && tab === 'posts' && (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {posts.length === 0 && (
            <p className="py-4 text-center text-xs" style={{ color: theme.colors.muted }}>
              No posts in this period
            </p>
          )}
          {posts.map((post) => (
            <PostActivityRow
              key={post.id}
              post={post}
              theme={theme}
              isConfirming={confirmDelete === post.id}
              onDelete={() => handleDeletePost(post.id)}
              onNavigate={() => {
                onNavigateToPost?.(post.id);
              }}
            />
          ))}
        </div>
      )}

      {/* Comments tab */}
      {!isLoading && tab === 'comments' && (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {comments.length === 0 && (
            <p className="py-4 text-center text-xs" style={{ color: theme.colors.muted }}>
              No replies in this period
            </p>
          )}
          {comments.map((comment) => (
            <CommentActivityRow
              key={comment.id}
              comment={comment}
              theme={theme}
              isConfirming={confirmDelete === comment.id}
              onDelete={() => handleDeleteComment(comment.id)}
              onNavigate={() => {
                onNavigateToPost?.(comment.post_id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PostActivityRow({
  post,
  theme,
  isConfirming,
  onDelete,
  onNavigate,
}: {
  post: MyActivityPost;
  theme: ClubTheme;
  isConfirming: boolean;
  onDelete: () => void;
  onNavigate: () => void;
}) {
  const snippet = post.body ? (post.body.length > 80 ? post.body.slice(0, 80) + '...' : post.body) : '(media only)';

  return (
    <div
      className="flex items-start gap-2 rounded-lg px-3 py-2 transition-colors hover:opacity-90"
      style={{ backgroundColor: `${theme.colors.muted}08`, border: `1px solid ${theme.colors.muted}10` }}
    >
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onNavigate}>
        <p className="text-xs truncate" style={{ color: theme.colors.text }}>
          {snippet}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          {post.channel && (
            <span className="text-[10px]" style={{ color: theme.colors.primary }}>
              #{post.channel.name}
            </span>
          )}
          <span className="text-[10px]" style={{ color: theme.colors.muted }}>
            {timeAgo(post.created_at)}
          </span>
          {post.reaction_count > 0 && (
            <span className="text-[10px]" style={{ color: theme.colors.muted }}>
              {post.reaction_count} reaction{post.reaction_count > 1 ? 's' : ''}
            </span>
          )}
          {post.comment_count > 0 && (
            <span className="text-[10px]" style={{ color: theme.colors.muted }}>
              {post.comment_count} comment{post.comment_count > 1 ? 's' : ''}
            </span>
          )}
          {post.shared_to_community && (
            <span className="text-[10px] font-medium" style={{ color: theme.colors.accent }}>
              Shared
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="shrink-0 rounded p-1 transition-colors hover:opacity-80"
        style={{
          color: isConfirming ? '#EF4444' : theme.colors.muted,
          backgroundColor: isConfirming ? '#EF444415' : 'transparent',
        }}
        title={isConfirming ? 'Click again to confirm delete' : 'Delete post'}
      >
        <TrashIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function CommentActivityRow({
  comment,
  theme,
  isConfirming,
  onDelete,
  onNavigate,
}: {
  comment: MyActivityComment;
  theme: ClubTheme;
  isConfirming: boolean;
  onDelete: () => void;
  onNavigate: () => void;
}) {
  const snippet = comment.body.length > 80 ? comment.body.slice(0, 80) + '...' : comment.body;

  return (
    <div
      className="flex items-start gap-2 rounded-lg px-3 py-2 transition-colors hover:opacity-90"
      style={{ backgroundColor: `${theme.colors.muted}08`, border: `1px solid ${theme.colors.muted}10` }}
    >
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onNavigate}>
        <p className="text-xs" style={{ color: theme.colors.text }}>
          {snippet}
        </p>
        <p className="mt-0.5 text-[10px] truncate" style={{ color: theme.colors.muted }}>
          replied to {comment.parent_post_author}: "{comment.parent_post_snippet.slice(0, 50)}..."
          <span className="ml-1">{timeAgo(comment.created_at)}</span>
        </p>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="shrink-0 rounded p-1 transition-colors hover:opacity-80"
        style={{
          color: isConfirming ? '#EF4444' : theme.colors.muted,
          backgroundColor: isConfirming ? '#EF444415' : 'transparent',
        }}
        title={isConfirming ? 'Click again to confirm delete' : 'Delete reply'}
      >
        <TrashIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
